import { apiBlobWithHeaders, apiRequest } from './httpClient';

// NOTE: there is deliberately no "end this meeting on page unload" call
// here. A browser refresh, a transient route teardown, a backend restart
// or a short network drop must NOT make an ACTIVE meeting terminal — the
// backend owns that decision. An open /ws/audio reconnects (a second
// socket to the same ACTIVE meeting is a valid reconnect), and a meeting
// whose tab really did go away for good is finalized by the backend's
// last_activity_at heartbeat + meeting_reaper after its grace period
// (MEETING_ABANDONED_AFTER_SECONDS). Ending a meeting is only ever an
// explicit user action (see LiveMeetingPage's handleEndMeeting).

export function createMeeting({
  title,
  sourceLanguage,
  targetLanguage,
  aiAnswerLanguage,
  mode,
  audioSource,
  documentIds,
  documentAiSettings,
  companyId,
  contextMode,
  contextMeetingIds,
  meetingType,
  programmingLanguages,
  transcriptionEnabled,
  aiPersonalizationOverride,
}) {
  // No stt_provider/tts_provider here: speech provider selection is a
  // backend-only deployment concern (DEFAULT_STT_PROVIDER/DEFAULT_TTS_
  // PROVIDER), never something the client chooses or sends. audioSource
  // IS client-controlled — it's a capture-pipeline choice (mic vs.
  // browser-tab vs. both), independent of speech provider. sourceLanguage
  // may be "auto" (SOURCE_LANGUAGE_AUTO); aiAnswerLanguage may be
  // "same_as_translation" (AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION) — both
  // are independent of translation's targetLanguage (see
  // constants/languages.js). documentIds is optional (existing AI
  // Documents, see services/documentService.js) - omitted entirely when
  // empty so meeting creation without documents sends the exact same
  // body it always has. companyId is likewise optional (backend/schemas/
  // meeting.py's MeetingCreate.company_id) - an existing company picked
  // in services/companyApi.js's selector, never a company_name here
  // since the "+ Create New Company" flow already creates the company
  // up front and selects its real id.
  return apiRequest('/api/meetings', {
    method: 'POST',
    body: {
      // This meeting's AI Response Style, only when the user chose one other
      // than their default (backend MeetingCreate.ai_personalization_override:
      // legacy / builtin / custom id - never instructions). Omitted = inherit
      // the saved account selection, exactly as before this field existed.
      ...(aiPersonalizationOverride
        ? { ai_personalization_override: aiPersonalizationOverride }
        : {}),
      title,
      source_language: sourceLanguage,
      target_language: targetLanguage,
      ai_answer_language: aiAnswerLanguage,
      mode,
      audio_source: audioSource,
      ...(documentIds && documentIds.length > 0 ? { document_ids: documentIds } : {}),
      // "Exact Document Answers" per selected document, [{ document_id,
      // use_for_ai_answers }] (backend MeetingCreate.document_ai_settings).
      // Left out when none is given, which the backend treats as before the
      // setting existed.
      ...(documentAiSettings && documentAiSettings.length > 0
        ? { document_ai_settings: documentAiSettings }
        : {}),
      ...(companyId ? { company_id: companyId } : {}),
      // context_mode is likewise optional (backend/schemas/meeting.py's
      // MeetingCreate.context_mode) - defaults to "automatic" server-
      // side when omitted, matching the behavior every meeting already
      // had before this field existed. context_meeting_ids is only
      // meaningful (and only sent) for the "manual" mode.
      ...(contextMode ? { context_mode: contextMode } : {}),
      ...(contextMode === 'manual' && contextMeetingIds && contextMeetingIds.length > 0
        ? { context_meeting_ids: contextMeetingIds }
        : {}),
      // AI answer context (backend/schemas/meeting.py's MeetingCreate.
      // meeting_type / programming_languages) - omitted at the defaults
      // ("general" / none = automatic), which the backend applies itself.
      ...(meetingType && meetingType !== 'general' ? { meeting_type: meetingType } : {}),
      ...(programmingLanguages && programmingLanguages.length > 0
        ? { programming_languages: programmingLanguages }
        : {}),
      // Meeting mode only (backend MeetingCreate.transcription_enabled):
      // whether this meeting may use paid speech-to-text. Left out for an
      // Interview, whose speech recognition is decided by the server, and
      // when the caller does not say (the server then defaults to OFF).
      ...(typeof transcriptionEnabled === 'boolean'
        ? { transcription_enabled: transcriptionEnabled }
        : {}),
    },
  });
}

// The browser's IANA zone name (e.g. "Asia/Kolkata") - only meaningful
// to the backend's range math (core/date_filters.get_meeting_date_range)
// when a date range is actually requested, since "today"/"this week"/
// "this month" mean the caller's local calendar day/week/month, not
// the server's. Falls back to UTC if Intl can't resolve one (very old
// browsers, locked-down environments).
function localTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// Returns { items, page, page_size, total, total_pages }.
export function listMeetings({ search, range, page = 1, pageSize = 20 } = {}) {
  const params = new URLSearchParams();

  if (search) params.set('search', search);
  if (range) {
    params.set('range', range);
    params.set('tz', localTimeZone());
  }
  params.set('page', page);
  params.set('page_size', pageSize);

  return apiRequest(`/api/meetings?${params.toString()}`);
}

// The caller's own meetings that are ACTIVE right now (an array, normally
// empty or one item). Called when the web app opens so the backend can
// raise its one-time "Meeting still active" notification - see
// components/layout/AppShell.jsx. It only reads status: it never ends,
// reconnects or resumes anything, and a meeting the backend's
// abandonment reaper already ended is simply not in the list.
export function getActiveMeetings() {
  return apiRequest('/api/meetings/active');
}

export function getMeeting(meetingId) {
  return apiRequest(`/api/meetings/${meetingId}`);
}

export function updateMeeting(meetingId, payload) {
  return apiRequest(`/api/meetings/${meetingId}`, {
    method: 'PATCH',
    body: payload,
  });
}

// Permanently deletes a meeting and everything it owns (backend:
// services/meeting_service.py's hard_delete_meeting) - a REAL delete,
// not the hide-only soft delete the rest of this app uses. The one
// endpoint both web and desktop call; see services/meetingDelete.js
// for the confirm-then-delete flow built on top of this. Resolves to
// null (204) on success; a still-ACTIVE/PAUSED meeting rejects with
// err.code === 'MEETING_STILL_ACTIVE'.
export function deleteMeeting(meetingId) {
  return apiRequest(`/api/meetings/${meetingId}`, { method: 'DELETE' });
}

export function getMeetingTranscripts(meetingId, { page = 1, pageSize = 100 } = {}) {
  const params = new URLSearchParams();
  params.set('page', page);
  params.set('page_size', pageSize);

  return apiRequest(`/api/meetings/${meetingId}/transcripts?${params.toString()}`);
}

export function getMeetingAiResponses(meetingId) {
  return apiRequest(`/api/meetings/${meetingId}/ai-responses`);
}

// Thumbs up/down on one AI answer. `feedback` is 'like' | 'dislike';
// `reason` is optional. Posting the rating the answer already has
// clears it (the button toggles off) - the backend decides that, and
// the response always carries the resulting state: { feedback: 'like'
// | 'dislike' | null }.
export function submitAiAnswerFeedback(
  answerId,
  { feedback, reason, reasonCode, answerRevision, applyPreference, preferenceValue } = {},
) {
  return apiRequest(`/api/meeting-ai-answers/${answerId}/feedback`, {
    method: 'POST',
    body: {
      feedback,
      ...(reason ? { reason } : {}),
      ...(reasonCode ? { reason_code: reasonCode } : {}),
      ...(answerRevision ? { answer_revision: answerRevision } : {}),
      ...(applyPreference ? { apply_preference: true } : {}),
      ...(preferenceValue ? { preference_value: preferenceValue } : {}),
    },
  });
}

export function getSummaryStats() {
  return apiRequest('/api/meetings/summary/stats');
}

// `rangeDays` scopes every figure (totals, languages, chart, previous
// period) to the last N days. `activityDays` is the legacy form that
// scopes only the daily chart; kept for older callers.
export function getAnalytics({ rangeDays, activityDays = 7 } = {}) {
  const params = new URLSearchParams();
  if (rangeDays) {
    params.set('range_days', rangeDays);
  } else {
    params.set('activity_days', activityDays);
  }

  return apiRequest(`/api/meetings/analytics?${params.toString()}`);
}

// AI-generated post-meeting analysis (backend/services/meeting_analysis.py) —
// triggered automatically when a meeting ends. getMeetingSummary rejects
// with a 404 (err.status === 404) until analysis has run at least once;
// callers treat that as "not available yet", not a real error - see
// components/meeting/MeetingSummaryPanel.jsx.
export function getMeetingSummary(meetingId) {
  return apiRequest(`/api/meetings/${meetingId}/summary`);
}

// PDF-only export of a COMPLETED summary (backend/routers/meetings.py) —
// the backend supplies the filename via Content-Disposition, so this
// never re-derives/re-sanitizes one client-side (see
// components/meeting/MeetingSummaryPanel.jsx's handleExportPdf).
export function exportMeetingSummaryPdf(meetingId) {
  return apiBlobWithHeaders(`/api/meetings/${meetingId}/summary/export/pdf`);
}

export function getActionItems(meetingId) {
  return apiRequest(`/api/meetings/${meetingId}/action-items`);
}

// Risks/follow-ups/other notable points extracted alongside the summary
// above - always a plain list (never 404), empty before analysis runs
// or if the AI found nothing worth surfacing.
export function getMeetingInsights(meetingId) {
  return apiRequest(`/api/meetings/${meetingId}/insights`);
}

export function updateActionItemStatus(actionItemId, status) {
  return apiRequest(`/api/action-items/${actionItemId}`, {
    method: 'PATCH',
    body: { status },
  });
}
